import {
	faArrowRight,
	faExclamationTriangle,
	faPencilAlt,
	faRetweet,
	faSpinner,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, publicApiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import SidePanel from "@/components/Sidepanel";
import { useAuthStore } from "@/store/auth.store";
import { Brand, useSystemBrandStore } from "@/store/systemBrand.store";
import { formatDateWithTime } from "@/utils/dateFormatter";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import AddBrandForm from "./AddBrandForm";

export interface CreateBrandRequest {
	brand: {
		name: string;
		origin: string;
	};
	user: {
		email: string;
	};
}

export interface UpdateBrandRequest {
	name: string;
}

export default function BrandPage() {
	const { success, error: showError } = useToast();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const {
		systemBrandToDelete,
		systemBrandToEdit,
		isDeleteModalOpen,
		isEditModalOpen,
		setSystemBrandToDelete,
		setSystemBrandToEdit,
		setIsDeleteModalOpen,
		setIsEditModalOpen
	} = useSystemBrandStore();
	const {
		isSuperAdmin,
		isBrand,
		brandId,
		brandName,
		setLogin,
		refreshToken,
		setAuth
	} = useAuthStore();

	const [formData, setFormData] = useState({
		name: "",
		email: "",
		origin: ""
	});

	const [switchBrandModalOpen, setSwitchBrandModalOpen] = useState(false);
	const [brandToSwitch, setBrandToSwitch] = useState<Brand | undefined>();

	const handleSwitchBrandClick = (brand: Brand) => {
		setSwitchBrandModalOpen(true);
		setBrandToSwitch(brand);
	};

	const getCurrentLoginDisplay = () => {
		if (isSuperAdmin === "true") return "Super Admin";

		if (isBrand === "true" && brandId) return brandName;

		return "Unknown";
	};

	const handleConfirmSwitch = async () => {
		if (!brandToSwitch) return;

		try {
			// Reinitiate token generation with new brandId
			const tokenRes = await publicApiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["AUTH", "REFRESH"]),
				{
					refreshToken,
					brandId: brandToSwitch.id.toString()
				}
			);

			const { accessToken, expiresAt } = tokenRes.data;

			// Only set auth and login if we get here (success case)
			if (refreshToken) {
				setAuth({
					accessToken,
					refreshToken,
					expiresAt: expiresAt.toString()
				});

				setLogin({
					isSuperAdmin: "false",
					isBrand: "true",
					brandId: brandToSwitch.id.toString(),
					brandName: brandToSwitch.name
				});

				success(`Successfully switched to brand: ${brandToSwitch.name}`);
				setSwitchBrandModalOpen(false);
				setBrandToSwitch(undefined);

				// Navigate to brand home page
				navigate(`/${brandToSwitch.id}/integrations`);
			}
		} catch (err) {
			const errorMessage =
				err && typeof err === "object" && "response" in err
					? (err as ApiError).response?.data?.error?.message
					: "Failed to switch brand. Please try again.";

			showError(errorMessage);
			setSwitchBrandModalOpen(false);
			setBrandToSwitch(undefined);
		}
	};

	const { data: brands = [], isLoading } = useQuery({
		queryKey: ["brands"],
		queryFn: async () => {
			const { data } = await apiClient.get<Brand[]>(
				resolveEndpoint(API_ENDPOINTS, ["BRANDS", "LIST"])
			);
			return data;
		}
	});

	const createMutation = useMutation({
		mutationFn: async (data: CreateBrandRequest) => {
			const { data: response } = await apiClient.post<Brand>(
				resolveEndpoint(API_ENDPOINTS, ["BRANDS", "CREATE"]),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["brands"] });
			success("Brand created successfully");
			handleCloseModal();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create brand");
		}
	});

	const updateMutation = useMutation({
		mutationFn: async ({
			id,
			data
		}: {
			id: number;
			data: UpdateBrandRequest;
		}) => {
			const { data: response } = await apiClient.put<Brand>(
				resolveEndpoint(API_ENDPOINTS, ["BRANDS", "UPDATE_BY_ID"], id),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["brands"] });
			success("Brand updated successfully");
			handleCloseModal();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update brand");
		}
	});

	const deleteMutation = useMutation({
		mutationFn: async (id: number) => {
			const { data } = await apiClient.delete(
				resolveEndpoint(API_ENDPOINTS, ["BRANDS", "DELETE_BY_ID"], id)
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["brands"] });
			success("Brand deleted successfully");
			setIsDeleteModalOpen(false);
			setSystemBrandToDelete(undefined);
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to delete brand");
		}
	});

	const handleEditClick = (brand: Brand) => {
		setSystemBrandToEdit(brand);
		setFormData({
			name: brand.name,
			email: "",
			origin: ""
		});
		setIsEditModalOpen(true);
	};

	const handleDeleteClick = (brand: Brand) => {
		setSystemBrandToDelete(brand);
		setIsDeleteModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsEditModalOpen(false);
		setSystemBrandToEdit(undefined);
		setFormData({ name: "", email: "", origin: "" });
	};

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleSubmit = () => {
		if (systemBrandToEdit) {
			updateMutation.mutate({
				id: systemBrandToEdit.id,
				data: { name: formData.name }
			});
		} else {
			createMutation.mutate({
				brand: { name: formData.name, origin: formData.origin },
				user: { email: formData.email }
			});
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	return (
		<div className="flex-1">
			<div className="flex justify-end items-center mb-6">
				<button
					type="button"
					onClick={() => setIsEditModalOpen(true)}
					className="bg-blue-fynxt text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
				>
					Onboard
				</button>
			</div>

			<div className="bg-white shadow-sm rounded-lg overflow-x-auto overflow-y-auto">
				<table className="min-w-full divide-y divide-gray-200 custom-table">
					<thead className="bg-gray-100 sticky top-0 z-10">
						<tr>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
							>
								Name
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
							>
								ID
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
							>
								Created At
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"
							>
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{brands.length ? (
							brands.map((brand) => (
								<tr key={brand.id} className="hover:bg-gray-50">
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{brand.name}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{brand.id}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{formatDateWithTime(brand.createdAt || "")}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center">
										<div className="flex justify-center space-x-2">
											<button
												type="button"
												onClick={() => handleSwitchBrandClick(brand)}
												className="text-blue-600 hover:text-blue-900 p-1"
												title="Switch to Brand"
											>
												<FontAwesomeIcon icon={faRetweet} />
											</button>
											<button
												type="button"
												onClick={() => handleEditClick(brand)}
												className="text-green-600 hover:text-green-900 p-1 ml-[5px]"
												title="Edit Brand"
											>
												<FontAwesomeIcon icon={faPencilAlt} />
											</button>
											<button
												type="button"
												onClick={() => handleDeleteClick(brand)}
												className="text-red-600 hover:text-red-900 p-1"
												title="Delete Brand"
											>
												<FontAwesomeIcon icon={faTrash} />
											</button>
										</div>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={4}
									className="px-6 py-6 text-center text-gray-400 text-sm whitespace-nowrap"
								>
									No brands data found.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<SidePanel
				isOpen={isEditModalOpen}
				onClose={handleCloseModal}
				title={systemBrandToEdit ? "Edit Brand" : "Onboard Brand"}
				closeOnOutsideClick={false}
			>
				<AddBrandForm
					formData={formData}
					isEditing={!!systemBrandToEdit}
					isSubmitting={createMutation.isPending || updateMutation.isPending}
					handleChange={handleChange}
					handleSubmit={handleSubmit}
					selectedBrand={systemBrandToEdit}
				/>
			</SidePanel>

			{isDeleteModalOpen && systemBrandToDelete && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity"
							aria-hidden="true"
						>
							<div className="absolute inset-0 bg-gray-500 opacity-75" />
						</div>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
										<FontAwesomeIcon
											icon={faExclamationTriangle}
											className="h-6 w-6 text-red-600"
										/>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Delete Brand
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to delete{" "}
												{systemBrandToDelete.name}? This action cannot be
												undone.
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => deleteMutation.mutate(systemBrandToDelete.id)}
									disabled={deleteMutation.isPending}
								>
									{deleteMutation.isPending ? (
										<>
											<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
											Deleting...
										</>
									) : (
										"Delete"
									)}
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => setIsDeleteModalOpen(false)}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{switchBrandModalOpen && brandToSwitch && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
							aria-hidden="true"
						/>

						<span
							className="hidden sm:inline-block sm:align-middle sm:h-screen"
							aria-hidden="true"
						>
							&#8203;
						</span>

						<div className="fixed inset-0 z-50 overflow-y-auto">
							<div className="flex items-start justify-center min-h-screen pt-6 px-4 text-center sm:block sm:p-0">
								<div className="inline-block w-full align-top bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-md sm:w-full">
									<div className="bg-white pb-3">
										<div className="sm:flex sm:items-start">
											<div className="mt-3 text-left sm:mt-0 w-full">
												<h3 className="bg-gray-50 border-b font-semibold leading-6 mb-3 p-4 text-gray-900 text-lg">
													<FontAwesomeIcon
														icon={faRetweet}
														className="text-gray-500 mr-2"
													/>
													Switch Brand
												</h3>

												<div className="flex flex-col gap-3 px-4 py-3 sm:flex-row items-center">
													<div className="flex-1 bg-gray-50 p-3 rounded border border-gray-200 hover:bg-gray-100">
														<p className="text-xs text-gray-500 mb-1 font-medium">
															Current Login
														</p>
														<p className="text-base font-semibold text-gray-800">
															{getCurrentLoginDisplay()}
														</p>
													</div>

													<div className="w-8 flex justify-center text-center">
														<FontAwesomeIcon
															icon={faArrowRight}
															className="text-blue-600 text-lg"
														/>
													</div>

													<div className="flex-1 bg-gray-50 p-3 rounded border border-gray-200 hover:bg-gray-100">
														<p className="text-xs text-gray-600 mb-1 font-medium">
															Switch to Brand
														</p>
														<p className="text-base font-semibold text-[#0099ff]">
															{brandToSwitch?.name || "Select a brand"}
														</p>
													</div>
												</div>
											</div>
										</div>
									</div>

									<div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse border-t border-gray-100">
										{(!brandId || brandToSwitch?.id.toString() !== brandId) && (
											<button
												type="button"
												className="w-full inline-flex justify-center items-center rounded px-4 py-1.5 text-sm bg-blue-fynxt text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-2 sm:w-auto transition-all duration-200"
												onClick={handleConfirmSwitch}
											>
												Yes, Switch
											</button>
										)}
										<button
											type="button"
											className="mt-2 sm:mt-0 w-full inline-flex justify-center items-center rounded border border-gray-300 px-4 py-1.5 bg-white text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-2 sm:w-auto transition-all duration-200"
											onClick={() => {
												setSwitchBrandModalOpen(false);
												setBrandToSwitch(undefined);
											}}
										>
											{!brandId || brandToSwitch?.id.toString() !== brandId
												? "Cancel"
												: "Close"}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
